import React from 'react';
import { Link } from 'react-router-dom';
import {
  FaMoneyBillWave, FaFileInvoiceDollar, FaLandmark,
  FaClock, FaChartBar, FaShieldAlt,
  FaUserTie, FaCheck, FaStar,
  FaFlag, FaSmile, FaTag, FaHeart,
  FaRocket, FaLock, FaPhoneAlt, FaClipboardCheck, FaChartLine, FaUsers,
} from 'react-icons/fa';

const features = [
  { icon: FaMoneyBillWave, title: 'Easy Pay Runs', desc: 'Run payroll in minutes. Automated salary calculations, deductions, and direct bank disbursements in just a few clicks.' },
  { icon: FaLandmark, title: 'Canadian Compliance', desc: 'CPP, EI, federal & provincial taxes computed and remitted automatically. Stay CRA audit-ready without the stress.' },
  { icon: FaFileInvoiceDollar, title: 'Smart Payslips', desc: 'Fully itemized, digital payslips delivered automatically to your employees via self-service portal and email.' },
  { icon: FaClock, title: 'Time & Leave Sync', desc: 'Sync hours worked, leaves, and statutory holidays directly into your pay run with zero copy-pasting.' },
  { icon: FaChartBar, title: 'Simple Reporting', desc: 'Real-time payroll registers, T4 summaries, department cost reports, and ROE generation in one click.' },
  { icon: FaShieldAlt, title: 'Bank-Grade Security', desc: 'Your payroll data is encrypted end-to-end with two-factor authentication and role-based access controls.' },
];

const stats = [
  { value: '10,000+', label: 'Businesses Served' },
  { value: '$500M+', label: 'Payroll Processed' },
  { value: '99.99%', label: 'Accuracy Rate' },
  { value: '4.9★', label: 'Client Rating' },
];

const testimonials = [
  { name: 'Sarah Mitchell', role: 'HR Manager, North Bay Contracting', text: 'Oriental Business Solutions cut our payroll processing time from 3 days to 30 minutes. CPP and EI are handled automatically — no more manual calculations.' },
  { name: 'James Tremblay', role: 'CFO, Maple Leaf Retail Co.', text: 'Managing payroll across multiple provinces was a nightmare. Oriental Business Solutions handles provincial tax rules automatically and generates ROEs in one click.' },
  { name: 'Emily Chandra', role: 'Founder, Cloud Business Services', text: 'The employee self-service portal saves us hours of back-and-forth. Staff log in, view payslips, and download their T4s on their own.' },
];

export default function Home() {
  React.useEffect(() => {
    document.title = "OrientalPay | Efficient Payroll Solutions";
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', 'Oriental Business Solutions delivers fast, accurate, and fully managed payroll for Canadian small businesses. CPP, EI, T4s, ROEs, and CRA remittances — all handled for you.');
    }
  }, []);

  return (
    <div className="min-h-screen bg-white">

      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex items-center pt-20 overflow-hidden">
        {/* Background Image */}
        <div className="absolute inset-0 z-0">
          <img
            src="/Home.jpg"
            alt="Oriental Business Solutions Accounting"
            className="w-full h-full object-cover object-center"
          />
          {/* Dark gradient overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/60 to-transparent" />
        </div>

        {/* Text content over the image */}
        <div className="relative z-10 max-w-6xl mx-auto px-6 w-full py-20 animate-slide-up text-white">
          <div className="max-w-2xl">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-sm border border-white/25 text-pto-yellow text-xs font-bold mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-pto-yellow animate-pulse" />
              Trusted by 10,000+ businesses across Canada
            </div>

            <h1 className="text-5xl md:text-6xl font-black leading-tight mb-5 text-white">
              Payroll,<br />
              <span className="text-pto-yellow">the easy way.</span>
            </h1>

            <p className="text-lg text-gray-200 mb-8 leading-relaxed max-w-lg">
              Simple, accurate, and on-time payroll services designed for Canadian small businesses. We run payroll, auto-calculate CPP, EI & CRA remittances, and pay your employees directly.
            </p>

            <div className="flex flex-wrap gap-4 mb-10">
              <Link to="/contact">
                <button className="px-8 py-3.5 btn-pto-yellow shadow-lg">
                  Book a Demo &gt;
                </button>
              </Link>
              <Link to="/pricing">
                <button className="px-8 py-3.5 btn-payday-blue shadow-lg">
                  Start Free Trial &gt;
                </button>
              </Link>
            </div>

            <div className="flex flex-wrap gap-5 text-sm text-gray-300 font-medium">
              {['No setup fees', '14-day free trial', 'Cancel anytime'].map(t => (
                <span key={t} className="flex items-center gap-1.5">
                  <FaCheck className="text-pto-yellow text-xs" /> {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>


      {/* About Oriental Business Solutions — Premium redesign */}
      <section className="py-24 px-6 section-pastel-blue">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-16 items-center">

          {/* Left: Image with floating stat card overlay */}
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-blue-900/10">
              <img
                src="/About.png"
                alt="Oriental Business Solutions in action"
                className="w-full object-cover"
              />
            </div>
            {/* Floating stats card */}
            <div className="absolute -bottom-6 -right-4 md:-right-8 bg-white rounded-2xl shadow-xl px-6 py-4 flex flex-col gap-1 border border-gray-100">
              <div className="text-2xl font-black text-payday-blue">$500M+</div>
              <div className="text-xs font-bold text-gray-400 uppercase tracking-wider">Payroll Processed</div>
            </div>
            {/* Floating badge */}
            <div className="absolute -top-4 -left-4 bg-pto-yellow rounded-2xl shadow-lg px-4 py-3 flex flex-col gap-0.5">
              <div className="text-xl font-black text-gray-900">10,000+</div>
              <div className="text-[10px] font-bold text-gray-700 uppercase tracking-wide">Happy Businesses</div>
            </div>
          </div>

          {/* Right: Text */}
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-pto-dark border border-yellow-200 text-xs font-bold mb-5 shadow-sm">
              About Our Payroll Service
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-5 leading-tight">
              We believe payroll should be easy — and a whole lot <span className="text-payday-blue">friendlier</span>
            </h2>
            <p className="text-gray-500 leading-relaxed mb-8 text-sm font-medium">
              Oriental Business Solutions is built for Canadian small businesses and the accountants and bookkeepers who support them. We handle CPP, EI, federal & provincial tax remittances behind the scenes. No guesswork, no spreadsheets—just stress-free payroll, backed by real people who really care.
            </p>

            <div className="flex flex-col gap-3 mb-8">
              <Link to="/pricing">
                <button className="px-7 py-3.5 btn-payday-blue shadow-md w-fit">
                  I'm a small business owner &gt;
                </button>
              </Link>
              <Link to="/contact">
                <button className="px-7 py-3.5 btn-pto-yellow shadow-sm w-fit">
                  I'm a bookkeeper or accountant &gt;
                </button>
              </Link>
            </div>

            {/* Trust bullets */}
            <div className="grid grid-cols-2 gap-3 text-sm">
              {[
                '14-day free trial, no card needed',
                'Setup in under 10 minutes',
                'Cancel anytime, no lock-in',
                'Real support from real humans',
              ].map(item => (
                <div key={item} className="flex items-start gap-2 text-gray-600 font-medium">
                  <FaCheck className="text-payday-blue text-xs mt-1 flex-shrink-0" />
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>

        </div>
      </section>



      <section className="py-14 px-6 border-y border-gray-100 bg-white">
        <div className="max-w-4xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map(({ value, label }) => (
            <div key={label} className="text-center">
              <div className="text-3xl md:text-4xl font-black text-payday-blue mb-1">{value}</div>
              <div className="text-sm font-semibold text-gray-500">{label}</div>
            </div>
          ))}
        </div>
      </section>


      {/* Why Oriental Business Solutions Section */}
      <section id="why-orientalpay" className="py-24 px-6 section-pastel-green">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16 animate-slide-up">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-green-700 border border-green-200 text-xs font-bold mb-4 shadow-sm">
              Why Choose Us
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-4 leading-tight">
              Why small businesses choose <span className="text-payday-blue">Oriental Business Solutions</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto font-medium">
              Running a business shouldn't feel like a compliance chore. Here is why thousands of Canadian companies trust us with their books and taxes.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Card 1: Made for Canada */}
            <div className="bg-white rounded-3xl p-6 border border-green-50 shadow-sm flex flex-col gap-4 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <FaFlag className="text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">100% Made for Canada</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We handle CPP, EI, federal & provincial taxes, and generate T4s and ROEs perfectly tailored to Canadian and CRA regulations.
              </p>
            </div>

            {/* Card 2: Zero Learning Curve */}
            <div className="bg-white rounded-3xl p-6 border border-green-50 shadow-sm flex flex-col gap-4 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <FaSmile className="text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Stress-Free Onboarding</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                We set up your bookkeeping and accounting systems for you. No software to learn—just pass us your invoices, and we handle the rest.
              </p>
            </div>

            {/* Card 3: No Hidden Fees */}
            <div className="bg-white rounded-3xl p-6 border border-green-50 shadow-sm flex flex-col gap-4 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <FaTag className="text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">No Hidden Fees</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Pay a predictable monthly flat fee. No surprise billing for phone calls or extra tax advice. Transparent and clear pricing structures.
              </p>
            </div>

            {/* Card 4: Real Support */}
            <div className="bg-white rounded-3xl p-6 border border-green-50 shadow-sm flex flex-col gap-4 hover:shadow-md hover:-translate-y-1 transition-all duration-300">
              <div className="w-12 h-12 rounded-2xl bg-green-50 flex items-center justify-center text-green-600">
                <FaHeart className="text-2xl" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">Human-First Support</h3>
              <p className="text-gray-500 text-sm leading-relaxed">
                Get support from real Canadian accounting experts who actually care about your business. No endless phone queues or automated bots.
              </p>
            </div>
          </div>
        </div>
      </section>


      {/* Value Prop 1: Friendly Compliance (Pastel Blue BG) */}
      <section className="py-20 px-6 section-pastel-blue">
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-payday-blue border border-blue-100 text-xs font-bold mb-4 shadow-sm">
              Compliance Solved
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-5 leading-tight">
              Payroll that speaks <span className="text-payday-blue">Canadian compliance</span>
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              Deductions, remittances, and CRA payroll audits can get complicated fast. Oriental Business Solutions takes the anxiety out of every payroll run.
            </p>
            <ul className="flex flex-col gap-3">
              {[
                'Automated calculations for CPP, EI, and federal/provincial income tax.',
                'Prompt year-end T4 preparation, filing, and electronic delivery.',
                'Automated calculations and on-time remittance of source deductions to the CRA.',
              ].map(f => (
                <li key={f} className="flex items-start gap-2.5 text-sm text-gray-700 font-medium">
                  <FaCheck className="text-payday-blue text-xs mt-1 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white p-8 rounded-3xl border border-blue-50/50 shadow-lg flex flex-col gap-4">
            <div className="text-xs font-bold text-gray-400 uppercase tracking-widest">Compliance Status</div>
            {[
              { label: 'CRA Remittances', desc: 'Source deductions auto-calculated and paid on time', status: 'Compliant' },
              { label: 'T4 Filing', desc: 'Year-end summaries and employee slips filed online', status: 'Compliant' },
              { label: 'ROE Submissions', desc: 'Record of Employment automatically sent to Service Canada', status: 'Compliant' },
            ].map(({ label, desc, status }) => (
              <div key={label} className="p-4 bg-pastel-blue rounded-2xl flex items-center justify-between gap-4">
                <div>
                  <div className="text-xs font-bold text-gray-800">{label}</div>
                  <div className="text-[10px] text-gray-500 mt-0.5">{desc}</div>
                </div>
                <span className="px-2.5 py-1 bg-green-100 text-green-700 text-[10px] font-bold rounded-full border border-green-200">
                  {status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-pastel-yellow border border-yellow-250 text-pto-dark text-xs font-bold mb-4">
              Our Core Features
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900 mb-3">
              Full-service <span className="gradient-text">payroll & compliance</span>
            </h2>
            <p className="text-gray-500 text-lg max-w-xl mx-auto font-medium">
              We handle calculations, deductions, and deposits so you can focus entirely on running and growing your business.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="card-wagepoint p-6 group">
                <div className="w-12 h-12 rounded-2xl bg-pastel-blue flex items-center justify-center mb-4 group-hover:scale-105 transition-transform duration-200">
                  <Icon className="text-payday-blue text-xl" />
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-payday-blue transition-colors">{title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Feature Highlights Section ─────────────────────────────────────── */}
      <section className="py-24 px-6 bg-[#1a3268] overflow-hidden relative">
        {/* Subtle dot-grid background */}
        <div className="absolute inset-0 opacity-5" style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }} />

        <div className="max-w-6xl mx-auto relative">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold mb-4 tracking-wider">
              WHY CHOOSE US
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-4 leading-tight">
              Features that set us <span className="text-pto-yellow">apart</span>
            </h2>
            <p className="text-blue-200 text-lg max-w-2xl mx-auto font-medium">
              We combine robust payroll systems with expert support to deliver a seamless payroll experience your business deserves.
            </p>
          </div>

          {/* Two-column layout: numbered list + visual checklist card */}
          <div className="grid md:grid-cols-2 gap-10 mb-16">

            {/* Left: Numbered Feature List */}
            <div className="flex flex-col gap-7">
              {[
                { num: '01', icon: FaRocket, title: 'Fast, Accurate Pay Runs', desc: 'Run payroll online in just a few clicks. Calculations, deductions, and deposits are executed instantly.' },
                { num: '02', icon: FaLock, title: 'Direct Deposit & Auto-Pay', desc: 'Secure direct deposits sent straight to employees\' bank accounts, eliminating paper checks and manual transfers.' },
                { num: '03', icon: FaPhoneAlt, title: 'Automatic CRA Remittances', desc: 'Source deductions (CPP, EI, tax) are computed and remitted to the CRA automatically on your behalf.' },
                { num: '04', icon: FaChartLine, title: 'Employee Self-Service', desc: 'Employees get their own secure portal access to view/download payslips, T4s, and update bank details.' },
              ].map(({ num, icon: Icon, title, desc }) => (
                <div key={num} className="flex gap-5 items-start group">
                  <div className="flex-shrink-0 w-14 h-14 rounded-2xl bg-white/10 border border-white/20 flex flex-col items-center justify-center group-hover:bg-pto-yellow group-hover:border-pto-yellow transition-all duration-300">
                    <span className="text-[9px] font-black text-white/50 group-hover:text-gray-900 leading-none">{num}</span>
                    <Icon className="text-white group-hover:text-gray-900 text-lg mt-0.5 transition-colors duration-300" />
                  </div>
                  <div>
                    <h3 className="text-white font-extrabold text-base mb-1 group-hover:text-pto-yellow transition-colors duration-200">{title}</h3>
                    <p className="text-blue-200 text-sm leading-relaxed font-medium">{desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Right: Visual Checklist Card */}
            <div className="bg-white rounded-3xl p-8 shadow-2xl flex flex-col gap-4 h-fit">
              <div className="flex items-center gap-3 mb-1">
                <div className="w-10 h-10 rounded-xl bg-[#3b5eb4] flex items-center justify-center">
                  <FaClipboardCheck className="text-white text-lg" />
                </div>
                <div>
                  <div className="font-black text-gray-900 text-base">Everything Included</div>
                  <div className="text-xs text-gray-400 font-semibold">No hidden add-ons or surprise billing</div>
                </div>
              </div>

              {[
                'Automated CPP & EI calculations',
                'Direct deposit & automated bank payouts',
                'CRA source deductions & remittance',
                'T4 & T4A slip generation & filing',
                'Record of Employment (ROE) filing',
                'Employee self-service portal',
                'Statutory holiday pay calculations',
                'Worker\'s Compensation Board (WCB) tracking',
                'Dedicated payroll support specialist',
              ].map((item) => (
                <div key={item} className="flex items-center gap-3">
                  <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <FaCheck className="text-green-600 text-[9px]" />
                  </div>
                  <span className="text-gray-700 text-sm font-semibold">{item}</span>
                </div>
              ))}

              <Link to="/contact" className="mt-3">
                <button className="w-full py-3.5 bg-[#3b5eb4] hover:bg-[#2d4d9e] text-white text-sm font-extrabold rounded-2xl transition-all shadow-lg hover:shadow-xl">
                  Book a Free Demo →
                </button>
              </Link>
            </div>
          </div>

          {/* How It Works — Process Steps */}
          <div className="border-t border-white/10 pt-14">
            <div className="text-center mb-12">
              <span className="inline-block px-4 py-1.5 rounded-full bg-white/10 border border-white/20 text-white text-xs font-bold mb-3 tracking-wider">
                HOW IT WORKS
              </span>
              <h3 className="text-3xl md:text-4xl font-black text-white">
                Get started in <span className="text-pto-yellow">3 simple steps</span>
              </h3>
            </div>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                { step: '1', icon: FaUsers, title: 'Set up your profile', desc: 'Input your company details and add your employees in minutes with our guide.' },
                { step: '2', icon: FaClipboardCheck, title: 'Connect your bank', desc: 'Securely link your business bank account for automated direct deposits and CRA remittances.' },
                { step: '3', icon: FaChartBar, title: 'Run payroll in minutes', desc: 'Verify hours worked, click run, and let us handle calculations, deposits, and filings.' },
              ].map(({ step, icon: Icon, title, desc }) => (
                <div key={step} className="bg-white/5 border border-white/10 rounded-3xl p-7 hover:bg-white/10 transition-all duration-300 group">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-12 h-12 rounded-2xl bg-pto-yellow flex items-center justify-center shadow-lg">
                      <Icon className="text-gray-900 text-xl" />
                    </div>
                    <span className="text-5xl font-black text-white/10 group-hover:text-white/20 transition-colors select-none">0{step}</span>
                  </div>
                  <h4 className="text-white font-extrabold text-base mb-2">{title}</h4>
                  <p className="text-blue-200 text-sm leading-relaxed font-medium">{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials (Pastel Yellow BG) */}
      <section className="py-24 px-6 section-pastel-yellow">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-white text-pto-dark border border-yellow-200 text-xs font-bold mb-4 shadow-sm">
              Client Stories
            </span>
            <h2 className="text-4xl md:text-5xl font-black text-gray-900">
              Trusted by <span className="text-payday-blue">Business Leaders</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map(({ name, role, text }) => (
              <div key={name} className="bg-white rounded-3xl p-6 border border-yellow-100 flex flex-col gap-4 shadow-sm">
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => <FaStar key={i} className="text-pto-yellow text-sm" />)}
                </div>
                <p className="text-gray-600 text-sm leading-relaxed flex-1 italic">"{text}"</p>
                <div className="flex items-center gap-3 pt-3 border-t border-gray-100">
                  <div className="w-9 h-9 rounded-full bg-pastel-blue flex items-center justify-center flex-shrink-0">
                    <FaUserTie className="text-payday-blue text-sm" />
                  </div>
                  <div>
                    <div className="font-bold text-sm text-gray-900">{name}</div>
                    <div className="text-xs text-gray-400 font-medium">{role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
